/**
 * ParentDashboardPage — the tabbed parent landing page at /parent
 * Replaces ParentWalletPage at the same ROUTES.PARENT_DASHBOARD ('/parent') route.
 *
 * Implements US-008 (T4): ParentDashboardPage with three tabs:
 *   Overview    — in-progress session banner + recent activity summary + child profiles grid
 *   Reports     — recent session reports (EmptyState when no reports available yet)
 *   Plan & Coins — full PlanAndCoinsTab (subscribe, buy packs, manage plan)
 *
 * Modified US-012 (T4): Plan & Coins tab now uses PlanAndCoinsTab component which
 *   adds subscribe/buy/manage Stripe actions and config-driven top-up packs grid.
 *   CoinBalancePanels (CCM-02) is preserved inside PlanAndCoinsTab verbatim.
 *
 * Modified US-013 (T3): Post-checkout banner integration.
 *   - Uses react-router useSearchParams to detect ?checkout=success on return from Stripe.
 *   - When detected: shows CheckoutReturnBanner above the tab bar, strips the marker from
 *     the URL immediately (setSearchParams replace:true) so refresh/back never re-triggers.
 *   - Schedules ONE delayed refetch of ['parent','dashboard'] at ~30s (webhook async lag)
 *     and auto-dismiss at ~60s, both via setTimeout with full useEffect cleanup.
 *   - Manual dismiss sets showCheckoutBanner=false immediately.
 *   - No banner on normal login (no query param) — AC-3 satisfied.
 *   - Banner only renders in the success branch (has coinBalance) — never during loading/error.
 *   KB TO-LL-013: marker read/strip via useSearchParams (not manual string parsing).
 *   KB TO-LL-036: refetch failures routed through existing ErrorState; never swallowed.
 *   KB TO-LL-007: CheckoutReturnBanner and WelcomeCreditBanner are distinct, separate messages;
 *                  no duplication (welcome banner rendered inside CoinBalancePanels, checkout
 *                  banner rendered above the tab bar — different code paths, different contexts).
 *
 * Modified US-014 (T3): RecentActivitySummary added to Overview tab replacing the standalone
 *   Coin Balance section. Passes coinBalance, children, and engagementSummary (surfaced by T1)
 *   to RecentActivitySummary. The component owns the coin display (CCM-02) so the standalone
 *   Coin Balance block is removed from the Overview tab to avoid duplication. Plan & Coins tab
 *   CoinBalancePanels is unchanged. No new API call — engagementSummary comes from the same
 *   ['parent','dashboard'] cache key via the updated useParentDashboard hook.
 *
 * Modified US-010 (T5): Real Reports tab replaces the hard-coded EmptyState placeholder.
 *   Added useParentSessions hook (React Query key ['parent','sessions'], staleTime 30s).
 *   Added selectedChildId local state (string|null) — set by per-child 'View reports' link,
 *   filters the Reports tab to the chosen child. Reports tab shows per-session highlights
 *   (strengths/improvements), honest activity counts (sessions completed, questions practised),
 *   and a scoped EmptyState ONLY when there are genuinely zero completed sessions.
 *   onViewReports prop wired into ChildProfilesGrid so each child card's "View reports"
 *   button sets activeTab='reports' and selectedChildId=child.userId.
 *   TO-LL-036: Reports tab shows ErrorState with retry on any fetch failure; never swallowed.
 *   AC-4: NO per-question-type accuracy/percentage text rendered anywhere on the page.
 *
 * Modified US-011 (T3): Reports-tab session list upgraded with ParentSessionRow.
 *   Replaced the inline per-session <div> map (lines 558-578) with <ParentSessionRow />
 *   which adds question count (AC-1), first-try fraction (AC-2), tappable report-open
 *   (AC-3), and correct cancelled rendering (AC-4). StarRating import retained for the
 *   latest-highlights card (unchanged). Cancelled sessions NOT filtered from list (AC-4).
 *
 * Modified US-004 (T5): Added isAddOpen / renameTarget local state; pass onAddChild and
 *   onRenameChild into ChildProfilesGrid; render AddChildDialog and RenameChildDialog.
 *   Mutations invalidate ['parent','dashboard'] so the grid auto-refreshes after create/rename.
 *
 * Modified US-002 (T-006): Added resetTarget state; pass onResetPassword into ChildProfilesGrid;
 *   render ResetChildPasswordDialog at page root. Mirrors renameTarget pattern ~95% verbatim.
 *
 * Modified US-005 (T4): Added isAddFirstChildOpen state; showTrialBanner derivation
 *   (children.length === 0 && !inProgressSession); TrialBanner rendered in Overview
 *   when zero children; AddFirstChildModal mounted at page root.
 *   TO-LL-013: no hardcoded path navigation added; modal open/close only.
 *   TO-LL-036: trial banner is purely presentational; all create errors handled inside modal.
 *   TO-LL-044: no camelCase violations introduced; no request/response shape changes.
 *
 * Implements US-006 (T6): original ParentWalletPage shell reused verbatim.
 *
 * CCM-02: coin buckets are always shown via CoinBalancePanels (two distinct panels,
 *   never merged). Enforced at render level — no combined total exists.
 * CCM-03: page mounts only inside ParentGuard route — students are redirected to
 *   /dashboard before this component ever renders.
 *
 * TO-LL-013: navigation constants reference ROUTES, not string literals.
 *
 * Data: single GET /parent/dashboard call via useParentDashboard (React Query cache
 * key ['parent','dashboard']). Tab switching is pure local useState — no extra API
 * calls on tab change (0 extra fetches, AC-4 performance guarantee).
 *
 * Error handling: errors are never swallowed. 401/403/500 surface via ErrorState
 * with a retry button that calls refetch() (CCM-03 fail-safe).
 *
 * Modified TS-017 (T7): the Overview "Your Coins" WalletPanel now receives
 * onTopupDetail={() => navigate(ROUTES.PARENT_TOPUP_COINS)}, wiring the "Top-up Coins"
 * tile to open the new parent-only detail screen. TO-LL-013: navigation goes through
 * the ROUTES constant, never a hardcoded path literal.
 *
 * Modified US-008 (T5): the "Share feedback" pill's onClick no longer assigns
 * window.location.href to a mailto: URL (removed under CCM-001) — it now opens
 * ParentFeedbackSheet, an in-app overlay. isFeedbackOpen / isFeedbackToastOpen
 * are the two new state flags; mostRecentSessionId is a useMemo derived from
 * the ALREADY-LOADED useParentSessions() query (DD-010), so this adds zero new
 * requests. The pill's markup, aria-label, icon, label and position are all
 * unchanged — only the onClick body changed. hello@thinkotter.sg now survives
 * as a mailto anchor ONLY inside ParentFeedbackSheet's failure block.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useParentDashboard } from '@/hooks/useParentDashboard';
import { useParentSessions } from '@/hooks/useParentSessions';
import { useUpdateChildDifficulty } from '@/hooks/useUpdateChildDifficulty';
import { CheckoutReturnBanner } from '@/components/CheckoutReturnBanner';
import { deriveLastActive } from '@/lib/parentActivity';
import { WalletPanel } from '@/components/WalletPanel';
import { WeeklyActivityStrip } from '@/components/WeeklyActivityStrip';
import { QuietHeroCard } from '@/components/QuietHeroCard';
import { FirstRunGuideCard } from '@/components/FirstRunGuideCard';
import { useStartChildSession } from '@/hooks/useStartChildSession';
import { WelcomeCreditBanner } from '@/components/CoinBalancePanels';
import { InProgressSessionBanner } from '@/components/InProgressSessionBanner';
import { ChildProfilesGrid } from '@/components/ChildProfilesGrid';
import { AddChildDialog } from '@/components/AddChildDialog';
import { RenameChildDialog } from '@/components/RenameChildDialog';
import { ResetChildPasswordDialog } from '@/components/ResetChildPasswordDialog';
import { AddFirstChildModal } from '@/components/AddFirstChildModal';
import { PlanAndCoinsTab } from '@/components/PlanAndCoinsTab';
import { ReferAFriendCard } from '@/components/ReferAFriendCard';
import { ParentFeedbackSheet } from '@/components/ParentFeedbackSheet';
import { Toast } from '@/components/ui/toast';
import { StarRating } from '@/components/StarRating';
import { ParentSessionRow } from '@/components/ParentSessionRow';
import { ViewFullReportButton } from '@/components/ViewFullReportButton';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { AppLogo } from '@/components/AppLogo';
import { LogoutButton } from '@/components/LogoutButton';
import { Mail } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { markWelcomeSeen } from '@/services/parent';
import { cn } from '@/lib/utils';
import type { ChildSummary, DifficultyTier } from '@/types/coins';

// ─── Tab definition ───────────────────────────────────────────────────────────

// TS-016 (T5): added 'refer' tab id — MUST stay the LAST entry in TABS below,
// after 'plan' (Plan & Coins), per AC-TS016.2.
type TabId = 'overview' | 'reports' | 'plan' | 'refer';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'reports', label: 'Reports' },
  { id: 'plan', label: 'Plan & Coins' },
  // TS-016 (T5), AC-TS016.2: 'Refer a Friend' must remain the LAST tab, after
  // Plan & Coins (the billing surface).
  { id: 'refer', label: 'Refer a Friend' },
];

// ─── ParentDashboardPage (main export) ───────────────────────────────────────

/**
 * ParentDashboardPage — /parent (ROUTES.PARENT_DASHBOARD).
 *
 * Owns:
 * - activeTab state (tab switching, no URL change, no extra API calls)
 * - welcome-banner first-login gating (shown once, then persisted server-side via
 *   POST /users/me/welcome-seen so it never reappears on any device)
 *
 * Uses useParentDashboard for all server state — same React Query cache key as
 * the old ParentWalletPage, so no stale data on navigation.
 */
export function ParentDashboardPage() {
  const {
    coinBalance,
    isNewFamily,
    children,
    inProgressSession,
    liveSessions,
    // first-run-guide (T4): childrenWithActivity inside this drives step 3. Already on the
    // ['parent','dashboard'] payload — surfaced by the hook, just never consumed here before.
    engagementSummary,
    activePlanName,
    subscriptionStatus,
    parentDisplayName,
    hasSeenWelcome,
    welcomeCoinAmount,
    serverTime,
    isLoading,
    error,
    refetch,
  } = useParentDashboard();

  // Redesign: parent identity for the greeting + header chip. Display-only — falls
  // back gracefully (name → email local-part → 'there') so a missing field never breaks.
  const { user } = useAuth();

  // TS-017 (T7): owns navigation to the top-up coin detail screen (AC-TS017.1).
  // TO-LL-013: path comes from ROUTES, never a hardcoded string literal.
  const navigate = useNavigate();
  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  })();
  // BUG-20260703-154123: use full authoritative name from GET /parent/dashboard.
  // Fallback chain: parentDisplayName (authoritative) → email local-part → 'there'.
  // No user?.name read and no whitespace-split — full name preserved as-is.
  const displayName = (() => {
    if (parentDisplayName) return parentDisplayName;
    const e = user?.email;
    if (e) return e.split('@')[0];
    return 'there';
  })();
  const userInitial = (parentDisplayName?.[0] || user?.email?.[0] || '?').toUpperCase();

  // US-013 (T3): Read the post-checkout query marker via react-router useSearchParams.
  // KB TO-LL-013: URL marker is read via useSearchParams, not manual string parsing.
  const [searchParams, setSearchParams] = useSearchParams();

  // US-013 (T3): Controls visibility of CheckoutReturnBanner (AC-1).
  // Default false — banner only shown when ?checkout=success is detected on mount.
  const [showCheckoutBanner, setShowCheckoutBanner] = useState(false);

  // US-013 (T3): Detect ?checkout=success and strip it from the URL immediately.
  // Runs when searchParams changes (including on first mount).
  // Stripping via setSearchParams({ replace: true }) means refresh/back never re-triggers
  // the banner (AC-2, AC-3). TO-LL-013: useSearchParams, not manual string parsing.
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      setShowCheckoutBanner(true);
      // Strip the marker: build new params without 'checkout' and replace history entry
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // US-013 (T3): When banner becomes visible, schedule one delayed refetch (~30s) and
  // auto-dismiss (~60s). Both timers are cleaned up on unmount or when banner hides
  // so no dangling timeout / setState-after-unmount warning occurs.
  // Deferred ~30s so the Stripe webhook (TS-008/TS-009) has time to write before we
  // re-read (eventual-consistency pattern, KB line 590).
  // KB TO-LL-036: refetch errors surface through the existing ErrorState; never swallowed.
  useEffect(() => {
    if (!showCheckoutBanner) return;
    const refetchTimer = setTimeout(() => {
      refetch();
    }, 30000);
    const dismissTimer = setTimeout(() => {
      setShowCheckoutBanner(false);
    }, 60000);
    return () => {
      clearTimeout(refetchTimer);
      clearTimeout(dismissTimer);
    };
  }, [showCheckoutBanner, refetch]);

  // US-013 (T3): Manual dismiss handler — immediately hides the banner.
  const handleDismissCheckoutBanner = () => setShowCheckoutBanner(false);

  // Tab state — Overview is the default landing tab (AC-1)
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // "Welcome to ThinkOtter!" banner appears on the FIRST login only, then never
  // again — persisted server-side (POST /users/me/welcome-seen) so it stays dismissed
  // across ALL of the parent's devices, not just this browser.
  //
  // Snapshot the server flag the first time it loads (React's derived-state capture
  // pattern, runs once). Snapshotting rather than reading the live value means a
  // background refetch — which returns hasSeenWelcome=true after we mark it seen —
  // does not make the banner vanish mid-session; it stays for this login and is gone
  // on the next. undefined = not loaded yet (banner deferred, not shown).
  const [seenAtFirstLoad, setSeenAtFirstLoad] = useState<boolean | undefined>(undefined);
  if (seenAtFirstLoad === undefined && hasSeenWelcome !== undefined) {
    setSeenAtFirstLoad(hasSeenWelcome);
  }

  // Session-only manual dismiss (immediate hide within this login).
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // Show only when the family qualifies AND the server says it has not been seen.
  const showWelcome = isNewFamily && seenAtFirstLoad === false && !welcomeDismissed;

  // The first time the banner is shown, persist "seen" server-side (once, guarded by
  // a ref accessed only inside the effect). Fire-and-forget — the banner is a low-
  // stakes nicety, so a failed write just means it may show once more (non-fatal).
  const welcomePersistedRef = useRef(false);
  useEffect(() => {
    if (showWelcome && !welcomePersistedRef.current) {
      welcomePersistedRef.current = true;
      void markWelcomeSeen().catch(() => {});
    }
  }, [showWelcome]);

  // Manual dismiss — hide immediately (persistence already fired on show).
  const handleDismissWelcome = () => {
    setWelcomeDismissed(true);
  };

  // US-004 (T5): Add-child dialog open state
  const [isAddOpen, setIsAddOpen] = useState(false);

  // US-004 (T5): Target child for rename dialog; null = dialog closed
  // RenameChildDialog renders null when child is null — safe if grid refetch removes the child
  const [renameTarget, setRenameTarget] = useState<ChildSummary | null>(null);

  // US-002 (T-006): Target child for reset-password dialog; null = dialog closed.
  // Mirrors renameTarget pattern: ResetChildPasswordDialog renders null when child is null.
  const [resetTarget, setResetTarget] = useState<ChildSummary | null>(null);

  // US-005 (T4): First-child onboarding modal open state
  const [isAddFirstChildOpen, setIsAddFirstChildOpen] = useState(false);

  // first-run-guide (T2): the parent self-trial banner was REMOVED here (Wayne, 2026-07-27,
  // "remove it. end of discussion about this. decision made and final").
  //
  // What went with it: showTrialBanner / trialCoinsAvailable / isSubscribed, the three banner
  // states, useStartTrialSession, and the useSessions() call that existed ONLY to spot an
  // in-progress trial (so this page now makes one fewer request).
  //
  // Why, in one line: it was a competing call to action telling a brand-new parent she did not
  // need to create the child, and it drew from the SAME family wallet as her child's first
  // session, with no already-trialled guard anywhere.
  //
  // ⚠️ RELEASE RULE: that banner's primary button was "Create my child & start" — the most
  // prominent way to add a child on an empty dashboard. It is only safe to remove because
  // FirstRunGuideCard's step 1 takes that job over. These two MUST ship together; shipping this
  // removal alone leaves a new parent with nothing obvious to press.

  // US-010 (T5): Selected child ID for the Reports tab filter.
  // null = show all children's sessions (default).
  // Set by the per-child 'View reports' button handler (handleViewReports).
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  // US-010 (T5): Fetch parent sessions for the Reports tab.
  // Separate React Query cache key ['parent','sessions'] — zero extra calls on tab switch
  // within staleTime. TO-LL-036: errors surface to Reports tab ErrorState; never swallowed.
  const {
    sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
  } = useParentSessions();

  // US-010 (T5): "View reports" handler — switches to Reports tab filtered to a specific child.
  // Called by each child card's "View reports" button (ChildProfilesGrid onViewReports prop).
  // No router navigation — tab switch only.
  const handleViewReports = (child: ChildSummary) => {
    setSelectedChildId(child.userId);
    setActiveTab('reports');
  };

  // US-008 (T5): feedback sheet + toast open state.
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isFeedbackToastOpen, setIsFeedbackToastOpen] = useState(false);

  // BUG-20260803-192629 (RC-002, fixed 2026-08-04): these three used to be
  // inline arrow functions passed directly as JSX props below, which meant
  // ParentFeedbackSheet and Toast received a NEW callback identity on every
  // page render. Combined with RC-001 (ParentFeedbackSheet's isSuccess-keyed
  // success effect), that turned the dismiss action itself into the render
  // that reversed it. useState setters are referentially stable, so an empty
  // dependency array is correct and complete here — no eslint-disable
  // needed. Follows the existing stabilised-dismiss-handler precedent at
  // SessionChatPage.tsx:687 (handlePopupDismiss) and :250
  // (handleDemoLimitDismiss).
  const handleCloseFeedback = useCallback(() => setIsFeedbackOpen(false), []);
  const handleFeedbackSuccess = useCallback(() => setIsFeedbackToastOpen(true), []);
  const handleDismissFeedbackToast = useCallback(() => setIsFeedbackToastOpen(false), []);

  // US-008 (T5, AC-008.5, DD-010): the parent's most recent session id across ALL
  // children, irrespective of status — NOT filtered to completed, because this is
  // the operator's diagnostic starting point, not a "latest report" pointer.
  // Sorts a COPY of the already-loaded `sessions` array (it belongs to the React
  // Query cache) DESCENDING by date, with null dates sorted LAST so a malformed
  // record can never win. ⚠️ This is the mirror image of firstCompletedSession's
  // ascending sort above — do not copy that sort direction here, or every
  // feedback submission would carry the OLDEST session id instead of the newest.
  const mostRecentSessionId = useMemo(() => {
    const sorted = [...sessions].sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    return sorted[0]?.sessionId;
  }, [sessions]);

  // first-run-guide (T4): the child's FIRST completed session, used by the guide's step 3 to
  // decide between the report button and the "being prepared" line.
  //
  // This reuses the sessions feed the page already loads for the weekly activity strip, so the
  // guide adds no request of its own. Note it draws on TWO already-loaded queries (dashboard +
  // sessions), not one.
  //
  // Oldest-first, because the guide is about her FIRST report, not her latest. Sessions with no
  // date sort last rather than being dropped, so a malformed record can never win.
  const firstCompletedSession = useMemo(() => {
    const completed = sessions
      .filter((s) => s.status === 'completed')
      .sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
    const first = completed[0];
    return first
      ? {
          sessionId: first.sessionId,
          reportAvailable: !!first.reportAvailable,
          // ⚠️ completedAt, NEVER `date`. `date` is the CREATION time, and the guide's safety
          // timer measured from it would delete the card the moment a session spread over a
          // weekend finished, taking the child's first report with it.
          completedAt: first.completedAt ?? null,
        }
      : null;
  }, [sessions]);

  // first-run-guide (T5): step 2's action. The parent stays signed in as herself throughout;
  // the session is created in the child's name. Separate from the student's own start button
  // because CCM-03's out-of-coins suppression is right for a child and wrong for a parent.
  const {
    startForChild,
    isStarting: isGuideStarting,
    error: guideStartError,
  } = useStartChildSession();
  const handleGuideStartSession = (child: ChildSummary) => startForChild(child.userId);

  // Which child, if any, has a session running right now. Used by the child cards to offer a
  // way INTO it rather than a start the backend would refuse (one active session per child).
  const liveSessionIdByChildId = useMemo(
    () =>
      liveSessions.reduce<Record<string, string>>((acc, s) => {
        acc[s.childUserId] = s.sessionId;
        return acc;
      }, {}),
    [liveSessions]
  );

  // ⚠️ Gates on isUnlimited, NEVER the balance alone. The Unlimited grant zeroes the monthly
  // bucket, so those families read {monthlyCoins: 0, isUnlimited: true} permanently — a naive
  // zero-check offers a top-up to the highest-paying customers forever. Same rule the guide
  // card uses; kept here rather than duplicated inside the grid so the two cannot drift.
  const outOfSessions =
    !!coinBalance &&
    !coinBalance.isUnlimited &&
    coinBalance.monthlyCoins + coinBalance.topupCoins === 0;

  // Only the card being started shows a spinner, rather than all of them.
  //
  // DERIVED, not synchronised. An earlier version cleared this in an effect when the mutation
  // finished, which is a cascading render and an extra source of truth for something the
  // mutation already knows. Reading the pending flag directly means there is nothing to clear:
  // when the request settles, this is null on the very next render.
  const [pendingChildId, setPendingChildId] = useState<string | null>(null);
  const startingChildId = isGuideStarting ? pendingChildId : null;
  const handleCardStartSession = (child: ChildSummary) => {
    setPendingChildId(child.userId);
    startForChild(child.userId);
  };

  // TS-007 (T10, SR6, AC-TS007.5): optimistic difficulty mutation, wired into
  // ChildProfilesGrid's per-card dropdown. onSetDifficulty forwards to updateDifficulty;
  // the hook itself owns the optimistic write + rollback on the ['parent','dashboard'] cache.
  const {
    updateDifficulty,
    error: difficultyError,
  } = useUpdateChildDifficulty();
  const handleSetDifficulty = (childId: string, difficultyTier: DifficultyTier) => {
    updateDifficulty({ childId, difficultyTier });
  };

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* ── Page header ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <AppLogo />
        <div className="flex items-center gap-2 md:gap-3">
          {/* Share Feedback — pill sits to the left of the identity chip (a grown-up action) */}
          <button
            type="button"
            onClick={() => setIsFeedbackOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-otter-sand bg-white px-3 py-1.5 text-[13px] font-semibold text-pebbles-500 shadow-sm transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
            aria-label="Share Feedback"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            <span>Share feedback</span>
          </button>
          {/* Identity chip — hidden on small screens to match the app's mobile header */}
          <div className="hidden items-center gap-2.5 rounded-full border border-otter-sand bg-white py-1 pl-3.5 pr-1.5 shadow-sm sm:flex">
            <span className="text-[13px] font-bold text-otter-earth-800">{displayName}</span>
            <span className="font-display flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-otter-orange-400 to-amber-500 text-[13px] font-extrabold text-white">
              {userInitial}
            </span>
          </div>
          <LogoutButton />
        </div>
      </header>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-6xl">
        {/* Title row — greeting on the left, tabs on the right (desktop); stacked on mobile */}
        <div className="flex flex-col gap-4 px-4 pb-1 pt-4 md:flex-row md:items-end md:justify-between md:px-6 md:pt-7">
          <div>
            <h1 className="font-display text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
              {greeting}, {displayName} <span aria-hidden="true">👋</span>
            </h1>
            <p className="mt-1 text-sm font-medium text-gray-500 md:text-[15px]">
              Here&apos;s how your learners are doing today.
            </p>
          </div>

          {/* Tab bar (AC-1: three tabs) — segmented pill, right-aligned on desktop.
              Rendered only in the success state (when the dashboard has loaded). */}
          {!isLoading && !error && coinBalance && (
            <div
              role="tablist"
              aria-label="Dashboard sections"
              className="inline-flex shrink-0 gap-1 self-start rounded-2xl border border-otter-sand bg-otter-sand/40 p-1.5 md:self-auto"
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                  id={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'h-10 rounded-xl px-4 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400',
                    activeTab === tab.id
                      ? 'bg-white text-otter-earth-800 shadow-sm'
                      : 'text-pebbles-500 hover:text-otter-earth-700'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Loading state — reused from ParentWalletPage ─────────────────── */}
        {isLoading && <LoadingState message="Loading your dashboard..." />}

        {/* ── Error state — errors are never swallowed (CCM-03 fail-safe) ──── */}
        {!isLoading && (error || !coinBalance) && (
          <div className="px-4 md:px-6">
            <ErrorState
              title="Could not load your dashboard"
              message={
                error
                  ? ((error as { message?: string }).message ?? 'Please try again.')
                  : 'Dashboard data is unavailable. Please try again.'
              }
              onRetry={refetch}
            />
          </div>
        )}

        {/* ── Success state — tab bar + active tab body ────────────────────── */}
        {!isLoading && !error && coinBalance && (
          <div className="px-4 pb-8 pt-3 md:px-6 md:pt-4">
            {/*
             * US-013 (T3): CheckoutReturnBanner — shown above the tab bar so it is
             * visible regardless of the active tab. Only rendered when the post-checkout
             * ?checkout=success marker was detected (AC-1). Not shown on normal login
             * or cancel return (AC-3). Banner is distinct from the WelcomeCreditBanner
             * inside CoinBalancePanels — different message, different code path (KB TO-LL-007).
             * KB TO-LL-036: if the auto-refetch fails, ErrorState handles the retry; banner
             * remains informational and the parent can still manually refresh.
             */}
            {showCheckoutBanner && (
              <CheckoutReturnBanner onDismiss={handleDismissCheckoutBanner} />
            )}

            {/* ── Overview tab ──────────────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div
                role="tabpanel"
                id="tabpanel-overview"
                aria-labelledby="tab-overview"
              >
                {/*
                 * Redesign: Welcome-credit banner (new families) rendered once above the hero
                 * row. Distinct from the checkout banner (KB TO-LL-007). CCM-02 unaffected.
                 */}
                {showWelcome && (
                  <WelcomeCreditBanner
                    amount={welcomeCoinAmount ?? coinBalance?.topupCoins ?? 1}
                    onDismiss={handleDismissWelcome}
                  />
                )}

                {/*
                 * first-run-guide (T4): the activation guide sits ABOVE the hero row, directly
                 * under the welcome banner. Deliberate placement — until the family has been
                 * through all three steps this is the only thing on the screen telling the
                 * parent what to do next, so it outranks the wallet.
                 *
                 * It retires itself once the family has finished (family-level, one run, never
                 * returns), so no visibility gate is needed here.
                 *
                 * NOTE it is mounted inside the already-loaded branch, which is what stops the
                 * guide flashing its unanswered state on every page load for parents who
                 * finished months ago. Do not hoist it above that gate.
                 */}
                <FirstRunGuideCard
                  children={children}
                  liveSessions={liveSessions}
                  engagementSummary={engagementSummary}
                  coinBalance={coinBalance}
                  firstCompletedSession={firstCompletedSession}
                  firstCompletedAt={firstCompletedSession?.completedAt ?? null}
                  serverNow={serverTime ?? null}
                  onAddChild={
                    children.length === 0
                      ? () => setIsAddFirstChildOpen(true)
                      : () => setIsAddOpen(true)
                  }
                  onStartSession={handleGuideStartSession}
                  onOpenLiveSession={(sessionId) => navigate(`/session/${sessionId}`)}
                  onSeePlans={() => setActiveTab('plan')}
                  isStarting={isGuideStarting}
                  startError={guideStartError}
                />

                {/*
                 * Hero row — the live-session card (or a calm placeholder when none) beside the
                 * wallet. Equal-height columns (lg:items-stretch) so the row never reads as
                 * lopsided. coinBalance is guaranteed non-null here (success-branch gate above).
                 * CCM-02: WalletPanel shows two distinct coin tiles via CoinBalancePanels.
                 */}
                <div className="mb-6 grid gap-5 lg:grid-cols-[1.5fr_1fr] lg:items-stretch">
                  {inProgressSession ? (
                    <InProgressSessionBanner
                      session={inProgressSession}
                      // first-run-guide (T8): a way in, not only a way to end it.
                      onOpenSession={(sessionId) => navigate(`/session/${sessionId}`)}
                    />
                  ) : (
                    <QuietHeroCard lastActive={deriveLastActive(children)} />
                  )}
                  <WalletPanel
                    balance={coinBalance}
                    onManage={() => setActiveTab('plan')}
                    onTopupDetail={() => navigate(ROUTES.PARENT_TOPUP_COINS)}
                  />
                </div>

                {/*
                 * TS-007 (T9, SR6, AC-TS007.4): "View all live sessions" link — shown
                 * ONLY when more than one child currently has a live English session
                 * (liveSessions embedded additively on the dashboard payload, API-004).
                 * Navigates to the dedicated listing page (ROUTES.PARENT_LIVE_SESSIONS),
                 * which renders one endable card per session.
                 * TO-LL-013: path comes from ROUTES, never a hardcoded string literal.
                 */}
                {liveSessions.length > 1 && (
                  <div className="mb-6 -mt-3">
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.PARENT_LIVE_SESSIONS)}
                      className="inline-flex h-11 items-center gap-1 rounded-lg px-1 text-sm font-semibold text-otter-orange-600 transition-colors hover:text-otter-orange-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
                    >
                      View all live sessions ({liveSessions.length}) <span aria-hidden="true">→</span>
                    </button>
                  </div>
                )}

                {/*
                 * "This week" activity strip — four real weekly metrics derived client-side
                 * from useParentSessions (no backend change). Shown only once there are
                 * children, since an all-zero strip adds nothing for brand-new families.
                 */}
                {children.length > 0 && (
                  <WeeklyActivityStrip sessions={sessions} totalChildren={children.length} />
                )}

                {/* Children grid — US-004 (T5): onAddChild and onRenameChild now wired */}
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="font-display text-xl font-semibold text-otter-earth-800">
                      My Children
                    </h2>
                    {children.length > 0 && (
                      <span className="rounded-full bg-otter-sand/60 px-3 py-1 text-xs font-bold text-pebbles-500">
                        {children.length} {children.length === 1 ? 'learner' : 'learners'}
                      </span>
                    )}
                  </div>
                  {/*
                   * US-004 (T5): Pass onAddChild and onRenameChild so ChildProfilesGrid
                   * activates the Add-a-child card and renders per-card Rename affordance.
                   * Mutations invalidate ['parent','dashboard'] — no manual refetch needed.
                   *
                   * US-005 (T4): When zero children, the Add-a-child card opens the
                   * AddFirstChildModal (onboarding flow). When one+ children exist,
                   * it opens the standard AddChildDialog. Both paths always have a live
                   * onAddChild callback — the "available soon" disabled state is gone.
                   *
                   * US-010 (T5): onViewReports handler added — each child card's "View reports"
                   * button calls handleViewReports which sets activeTab='reports' and
                   * selectedChildId=child.userId to pre-filter the Reports tab.
                   *
                   * TS-007 (T10, SR6): onSetDifficulty wired — each child card's compact
                   * difficulty dropdown calls handleSetDifficulty, which applies optimistically
                   * (useUpdateChildDifficulty) and rolls back on failure. The mutation error
                   * is surfaced once below the grid (TO-LL-036 — never swallowed).
                   */}
                  <ChildProfilesGrid
                    children={children}
                    onAddChild={
                      children.length === 0
                        ? () => setIsAddFirstChildOpen(true)
                        : () => setIsAddOpen(true)
                    }
                    onRenameChild={setRenameTarget}
                    onResetPassword={setResetTarget}
                    onViewReports={handleViewReports}
                    onSetDifficulty={handleSetDifficulty}
                    onStartSession={handleCardStartSession}
                    liveSessionIdByChildId={liveSessionIdByChildId}
                    onOpenSession={(sessionId) => navigate(`/session/${sessionId}`)}
                    outOfSessions={outOfSessions}
                    startingChildId={startingChildId}
                  />
                  {difficultyError && (
                    <p className="mt-2 text-xs text-red-600" role="alert">
                      Couldn't update difficulty. Please try again.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Reports tab ───────────────────────────────────────────────── */}
            {/*
             * US-010 (T5): Real Reports tab — replaces the hard-coded EmptyState placeholder.
             * Reads sessions from useParentSessions (React Query key ['parent','sessions']).
             * Filters by selectedChildId when set (from 'View reports' child-card link).
             * Shows: per-session highlights (strengths up to 3, improvements up to 2),
             *   honest activity counts (sessions completed, questions practised).
             * AC-4: NO per-question-type accuracy/percentage text is rendered.
             * TO-LL-036: ErrorState with retry rendered on any fetch failure; never swallowed.
             * EmptyState shown ONLY when there are genuinely zero completed sessions.
             */}
            {activeTab === 'reports' && (
              <div
                role="tabpanel"
                id="tabpanel-reports"
                aria-labelledby="tab-reports"
              >
                {/* Child filter selector — shown when there are children to filter by */}
                {children.length > 1 && (
                  <div className="mb-4 flex items-center gap-2">
                    <label htmlFor="child-filter" className="text-xs font-medium text-gray-600">
                      Filter by child:
                    </label>
                    <select
                      id="child-filter"
                      value={selectedChildId ?? ''}
                      onChange={(e) => setSelectedChildId(e.target.value || null)}
                      className="text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
                    >
                      <option value="">All children</option>
                      {children.map((c) => (
                        <option key={c.userId} value={c.userId}>
                          {c.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Loading state */}
                {sessionsLoading && <LoadingState message="Loading reports..." />}

                {/* Error state — errors never swallowed (TO-LL-036) */}
                {!sessionsLoading && sessionsError && (
                  <ErrorState
                    title="Could not load reports"
                    message={
                      (sessionsError as { message?: string }).message ?? 'Please try again.'
                    }
                    onRetry={refetchSessions}
                  />
                )}

                {/* Success state */}
                {!sessionsLoading && !sessionsError && (() => {
                  // Filter sessions by selected child (using userId matched to childDisplayName
                  // via the children list from useParentDashboard — exact display-name match).
                  const selectedChild = selectedChildId
                    ? children.find((c) => c.userId === selectedChildId)
                    : null;
                  const filtered = selectedChild
                    ? sessions.filter((s) => s.childDisplayName === selectedChild.displayName)
                    : sessions;

                  // Compute honest activity counts from the filtered sessions.
                  // AC-4: ONLY sessions-completed, questions-practised counts. No accuracy %.
                  const completedSessions = filtered.filter((s) => s.status === 'completed');
                  const sessionsCompleted = completedSessions.length;
                  const questionsPractised = completedSessions.reduce(
                    (sum, s) => sum + (s.questionCount ?? 0), 0
                  );

                  if (filtered.length === 0) {
                    return (
                      <EmptyState
                        message={
                          selectedChild
                            ? `No reports yet for ${selectedChild.displayName}. Complete a tutoring session to see their report here.`
                            : 'No reports yet. Complete a tutoring session to see your child\'s report here.'
                        }
                      />
                    );
                  }

                  // Latest session (most-recent first from backend)
                  const latest = filtered[0];

                  return (
                    <div className="space-y-4">
                      {/* ── Latest highlights card ─────────────────────────────── */}
                      <div className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-sm font-semibold text-gray-800">
                            {latest.childDisplayName
                              ? `${latest.childDisplayName}'s latest report`
                              : 'Latest report'}
                          </h3>
                          {latest.starRating != null && (
                            <StarRating rating={latest.starRating} size="sm" />
                          )}
                        </div>

                        {latest.passageTitle && (
                          <p className="text-xs text-gray-500 mb-3 italic">
                            Passage: {latest.passageTitle}
                          </p>
                        )}

                        {/* Strengths (up to 3) — AC-4: NO accuracy percentages */}
                        {Array.isArray(latest.strengths) && latest.strengths.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-green-700 mb-1">
                              Doing well
                            </p>
                            <ul className="space-y-0.5">
                              {latest.strengths.slice(0, 3).map((s, i) => (
                                <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                  <span className="text-green-500 mt-0.5" aria-hidden="true">✓</span>
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Improvements (up to 2) — AC-4: NO accuracy percentages */}
                        {Array.isArray(latest.improvements) && latest.improvements.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-amber-700 mb-1">
                              Worth practising
                            </p>
                            <ul className="space-y-0.5">
                              {latest.improvements.slice(0, 2).map((imp, i) => (
                                <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                  <span className="text-amber-500 mt-0.5" aria-hidden="true">→</span>
                                  {imp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* View full report CTA — only when the report has been
                            generated (mirrors ParentSessionRow gating). Opens the
                            full HTML report via GET /sessions/{sessionId}/report-url. */}
                        {latest.status === 'completed' && latest.reportAvailable === true && (
                          <ViewFullReportButton sessionId={latest.sessionId} />
                        )}
                      </div>

                      {/* ── Honest activity counts ─────────────────────────────── */}
                      {/* AC-3: sessions completed + questions practised (honest counts only). */}
                      {/* AC-4: NO per-question-type accuracy percentages anywhere here.      */}
                      <div className="rounded-2xl bg-otter-paper border border-gray-200 p-4">
                        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                          Activity summary
                        </h3>
                        <div className="flex gap-6">
                          <div className="text-center">
                            <p className="font-display text-xl font-bold text-otter-earth-800">{sessionsCompleted}</p>
                            <p className="text-xs text-gray-500">Sessions completed</p>
                          </div>
                          <div className="text-center">
                            <p className="font-display text-xl font-bold text-otter-earth-800">{questionsPractised}</p>
                            <p className="text-xs text-gray-500">Questions practised</p>
                          </div>
                        </div>
                      </div>

                      {/* ── Session list ───────────────────────────────────────── */}
                      {/* US-011 (T3): replaced inline <div> map with ParentSessionRow.
                          Adds question count (AC-1), first-try fraction (AC-2),
                          tappable report-open (AC-3), cancelled handling (AC-4).
                          Cancelled sessions are NOT filtered out here — they must appear
                          in the list with a 'Cancelled' label and no star (CCM-01 / AC-4). */}
                      <div className="space-y-2">
                        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          All sessions
                        </h3>
                        {filtered.map((s) => (
                          <ParentSessionRow key={s.sessionId} session={s} />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Plan & Coins tab ──────────────────────────────────────────── */}
            {activeTab === 'plan' && (
              <div
                role="tabpanel"
                id="tabpanel-plan"
                aria-labelledby="tab-plan"
              >
                {/*
                 * US-012 (T4): PlanAndCoinsTab replaces the previous CoinBalancePanels-only
                 * content. CoinBalancePanels (CCM-02) is reused verbatim inside PlanAndCoinsTab.
                 * Subscribe, buy top-up packs, and manage plan actions are all here.
                 * CCM-03: this component only renders inside ParentGuard-protected page.
                 */}
                <PlanAndCoinsTab
                  balance={coinBalance}
                  isNewFamily={showWelcome}
                  onDismissWelcome={handleDismissWelcome}
                  welcomeCoinAmount={welcomeCoinAmount}
                  planName={activePlanName}
                  subscriptionStatus={subscriptionStatus}
                />
              </div>
            )}

            {/* ── Refer a Friend tab ────────────────────────────────────────── */}
            {/*
             * TS-016 (T5), AC-TS016.2: last tab panel, after Plan & Coins. The card
             * owns its own fetch via useMyReferral (separate cache key
             * ['referral','me']) — no change to useParentDashboard and no extra
             * fetch until this tab is opened.
             */}
            {activeTab === 'refer' && (
              <div role="tabpanel" id="tabpanel-refer" aria-labelledby="tab-refer">
                <ReferAFriendCard />
              </div>
            )}
          </div>
        )}
      </main>

      {/*
       * US-004 (T5): Add and Rename dialogs — rendered at page root so z-50 overlay
       * covers the full viewport regardless of which tab is active.
       * Both dialogs manage their own error state and call mutation.reset() on open.
       * Mutations invalidate ['parent','dashboard'] on success — grid auto-refreshes.
       * TO-LL-036: dialogs stay open on error and render the backend message inline.
       */}
      <AddChildDialog
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
      <RenameChildDialog
        isOpen={renameTarget !== null}
        child={renameTarget}
        onClose={() => setRenameTarget(null)}
      />
      {/*
       * US-002 (T-006): ResetChildPasswordDialog — reset a child's password.
       * Rendered at page root (z-50) so it covers the full viewport.
       * Mirrors the RenameChildDialog render block ~95% verbatim (isOpen/child/onClose).
       * TO-LL-036: all reset errors are handled inside the dialog; never swallowed here.
       * TO-LL-044: body key `password` enforced by the dialog + service types.
       */}
      <ResetChildPasswordDialog
        isOpen={resetTarget !== null}
        child={resetTarget}
        onClose={() => setResetTarget(null)}
      />

      {/*
       * US-005 (T4): AddFirstChildModal — onboarding modal for zero-children parents.
       * Rendered at page root so z-50 overlay covers the full viewport.
       * TO-LL-036: all create errors are handled inside the modal; never swallowed here.
       * TO-LL-013: modal is open/close only — no route navigation.
       */}
      <AddFirstChildModal
        isOpen={isAddFirstChildOpen}
        onClose={() => setIsAddFirstChildOpen(false)}
      />

      {/*
       * US-008 (T5): ParentFeedbackSheet + Toast — rendered at page root so the
       * z-50/z-[60] overlays cover the full viewport regardless of active tab.
       * accountEmail is display only inside the sheet, never sent in the body.
       * The toast copy is fixed by BR-008.3 — exact string, full stops included,
       * never reworded here.
       */}
      <ParentFeedbackSheet
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
        accountEmail={user?.email}
        mostRecentSessionId={mostRecentSessionId}
        onSuccess={handleFeedbackSuccess}
      />
      <Toast
        open={isFeedbackToastOpen}
        message="Thanks. We read every message."
        onDismiss={handleDismissFeedbackToast}
      />
    </div>
  );
}
